// Phase 10 — Affiliate referral capture & attribution
//
// Reads ?ref=<code> from URL → persists in localStorage for 30 days +
// fires /api/public/affiliate/click. Also exposes `useTrackProductView`
// so any product page can record a view against the active ref.
import { useEffect } from "react";
import api from "@/lib/api";

const REF_KEY = "lifted_ref";
const VISITOR_KEY = "lifted_visitor_id";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function getVisitorId() {
  let v = localStorage.getItem(VISITOR_KEY);
  if (!v) {
    v = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(VISITOR_KEY, v);
  }
  return v;
}

function readRef() {
  try {
    const raw = localStorage.getItem(REF_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ref || !parsed?.at) return null;
    if (Date.now() - parsed.at > TTL_MS) return null;
    return parsed.ref;
  } catch (_) { return null; }  // eslint-disable-line no-unused-vars
}

function writeRef(ref) {
  try {
    localStorage.setItem(REF_KEY, JSON.stringify({ ref, at: Date.now() }));
  } catch (_) {}  // eslint-disable-line no-unused-vars
}

export function getActiveRef() {
  return readRef();
}

export function useCaptureRef() {
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      if (ref) {
        writeRef(ref);
        const visitor_id = getVisitorId();
        api.post("/public/affiliate/click", {
          ref,
          visitor_id,
          landing_url: window.location.pathname + window.location.search,
          utm_campaign: params.get("utm_campaign") || null,
          utm_source:   params.get("utm_source") || null,
          product_handle: params.get("product") || null,
        }).catch(() => {});
      }
    } catch (_) {}  // eslint-disable-line no-unused-vars
  }, []);
}

export function useTrackProductView(productHandle) {
  useEffect(() => {
    if (!productHandle) return;
    const visitor = getVisitorId();
    const ref = readRef();
    // Universal tracker — fires for every viewer, affiliate or not.
    api.post("/public/track/product-view", {
      product_handle: productHandle, visitor_id: visitor, ref,
    }).catch(() => {});
    // Affiliate-attributed tracker — only fires when there's a ref present.
    if (ref) {
      api.post("/public/affiliate/product-view", {
        ref, visitor_id: visitor, product_handle: productHandle,
      }).catch(() => {});
    }
  }, [productHandle]);
}
